import React from 'react';
import { FiSun, FiMoon } from 'react-icons/fi';
import { HiOutlineUserGroup } from 'react-icons/hi2';

const formatWindowTime = (windowStr) => {
  if (!windowStr) return '';
  const regex = /^([^(]+)\(([^)]+)\)$/;
  const match = windowStr.match(regex);
  if (!match) return windowStr;

  const name = match[1].trim();
  const range = match[2].trim();
  const times = range.split('-');
  if (times.length !== 2) return windowStr;

  const formatTime = (timeStr) => {
    const trimmed = timeStr.trim();
    const parts = trimmed.split(':');
    if (parts.length !== 2) return trimmed;
    let hour = parseInt(parts[0], 10);
    const minute = parts[1];
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12;
    hour = hour ? hour : 12;
    return `${hour}:${minute} ${ampm}`;
  };

  const formattedStart = formatTime(times[0]);
  const formattedEnd = formatTime(times[1]);

  return `${name} (${formattedStart} - ${formattedEnd})`;
};

const UserRetentionCard = ({ analyticsData, isLoading }) => {
  if (isLoading || !analyticsData) {
    return (
      <div className="bg-white border border-gray-100 rounded-[24px] p-6 shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)] hover:shadow-[0px_12px_24px_-4px_rgba(16,24,40,0.08),0px_4px_8px_-2px_rgba(16,24,40,0.03)] transition-all duration-500 ease-in-out flex flex-col justify-between h-full space-y-6">
        <div className="pb-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-[#111827] flex items-center gap-2">
            <HiOutlineUserGroup className="text-[#CC9900] text-xl" /> User Retention & Peak Hours
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">Customer loyalty ratio and peak activity windows.</p>
        </div>
        <div className="flex-grow flex items-center justify-center py-6">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-[3px] border-gray-100 border-t-[#CC9900] rounded-full animate-spin" />
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest animate-pulse">Collecting Report...</p>
          </div>
        </div>
      </div>
    );
  }

  const retention = analyticsData?.retention || {};
  const totalUnique = retention.totalUniqueUsers || 0;
  const loyalUsers = retention.loyalUsers || 0;
  const newUsers = retention.newUsers || 0;

  const returningPct = totalUnique > 0 ? Math.round((loyalUsers / totalUnique) * 100) : 0;
  const newPct = totalUnique > 0 ? Math.round((newUsers / totalUnique) * 100) : 0;

  const peakWindows = (analyticsData?.peakWindows || [])
    .filter((w) => w.totalSessions > 0);
  const sortedWindows = [...peakWindows].sort((a, b) => b.totalSessions - a.totalSessions);

  const topWindow1 = sortedWindows[0] || null;
  const topWindow2 = sortedWindows[1] || null;

  return (
    <div className="bg-white border border-gray-100 rounded-[24px] p-6 shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)] hover:shadow-[0px_12px_24px_-4px_rgba(16,24,40,0.08),0px_4px_8px_-2px_rgba(16,24,40,0.03)] transition-all duration-500 ease-in-out flex flex-col justify-between h-full space-y-4">
      <div>
        <div className="pb-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-[#111827] flex items-center gap-2">
            <HiOutlineUserGroup className="text-[#CC9900] text-xl" /> User Retention & Peak Hours
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">Customer loyalty ratio and peak activity windows.</p>
        </div>

        {/* Retention Meter */}
        <div className="mt-4 p-4 rounded-2xl bg-gray-50/80 border border-gray-100 space-y-3">
          <div className="flex items-center justify-between text-xs font-bold">
            <span className="text-gray-700">Returning Loyal Users</span>
            <span className="text-[#CC9900] text-sm">{returningPct}%</span>
          </div>
          <div className="w-full bg-gray-200 h-2.5 rounded-full overflow-hidden flex">
            <div
              className="bg-[#CC9900] h-full rounded-full transition-all duration-1000"
              style={{ width: `${returningPct}%` }}
            />
            <div
              className="bg-blue-400 h-full rounded-full transition-all duration-1000"
              style={{ width: `${newPct}%` }}
            />
          </div>
          <div className="flex items-center justify-between text-[11px] text-gray-400 font-medium">
            <span>Loyal: {returningPct}%</span>
            <span>New: {newPct}%</span>
          </div>
        </div>

        {/* Peak Hours Info */}
        <div className="mt-4 space-y-2.5">
          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Peak Engagement Windows</h4>
          
          {topWindow1 ? (
            <div className="flex gap-1 max-sm:flex-wrap items-center justify-between p-3 rounded-xl bg-amber-50/60 border border-amber-100 text-xs">
              <div className="flex items-center gap-2.5 text-amber-900 font-semibold">
                <FiMoon className="text-amber-600 text-base" />
                <span>{formatWindowTime(topWindow1.window)}</span>
              </div>
              <span className="font-extrabold text-amber-700 whitespace-nowrap">{topWindow1.totalSessions} sessions</span>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100 text-xs text-gray-400">
              <span>No active peak hours during this period</span>
            </div>
          )}

          {topWindow2 && (
            <div className="flex gap-1 max-sm:flex-wrap items-center justify-between p-3 rounded-xl bg-blue-50/60 border border-blue-100 text-xs">
              <div className="flex items-center gap-2.5 text-blue-900 font-semibold">
                <FiSun className="text-blue-600 text-base" />
                <span>{formatWindowTime(topWindow2.window)}</span>
              </div>
              <span className="font-extrabold text-blue-700 whitespace-nowrap">{topWindow2.totalSessions} sessions</span>
            </div>
          )}
        </div>
      </div>

      <div className="pt-3 border-t border-gray-100 flex items-center justify-end text-xs text-gray-500">
        <span className="font-bold text-gray-400">Retention Overview</span>
      </div>
    </div>
  );
};

export default React.memo(UserRetentionCard);
